<template>
  <div class="codeblock-container">
    <pre ref="pre" :class="`${languageClass} pre`"><code :class="`${languageClass} code`" v-html="html" /></pre>
    <div class="copy-btn-container">
      <v-tooltip text="Copy code" location="top">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            icon="mdi-content-copy"
            size="small"
            variant="text"
            color="primary"
            @click="copyCode"
            aria-label="Copy code"
          />
        </template>
      </v-tooltip>
    </div>
  </div>
</template>
<script lang="ts">
import { Options, Vue } from 'vue-class-component'
// import { mapGetters, mapState } from 'vuex'
// import { marked } from 'marked'
import Prism from 'prismjs'
import 'prismjs/themes/prism-okaidia.css'

@Options({
  props: {
    code: String,
    grammar: String,
    language: String,
    lineStart: Number,
    lineNumbers: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    code() {
      (this as any).recompute()
    },
    language() {
      (this as any).recompute()
    }
  }
})
export default class CodeBlock extends Vue {
  public html: string = ''
  public code!: string
  public grammar!: string
  private grammar_: string = ''
  public language!: string
  private language_: string = ''
  public pre!: HTMLPreElement
  public languageClass: string = ''
  private defaultLanguage: string = 'javascript'
  private defaultGrammar: string = 'javascript'
  private lineStart!: number
  private lineNumbers!: boolean

  created() {
    this.highlight()
    this.languageClass = `language-${this.language}`
  }

  mounted() {
    this.pre = this.$refs.pre as HTMLPreElement
    if (this.lineNumbers) {
      this.pre.className = this.pre.className + ' with-line-numbers'
    } else {
      this.pre.className = this.pre.className + ' without-line-numbers'
    }
    this.renderLineNumbers()
  }

  highlight() {
    this.language_ = this.language == null ? 'javascript' : this.language
    if (this.language_.toLowerCase() === 'vue') {
      this.language_ = 'html'
    }
    this.grammar_ = this.grammar == null ? this.language_ : this.grammar
    const grammar = Prism.languages[this.grammar_]
    try {
      const src = this.code ?? ''
      this.html = Prism.highlight(src, grammar, this.language_)
    } catch (e) {
      if (e != null && (e as Error).message != null) {
        console.log((e as Error).message)
        if ((e as Error).message.includes('has no grammar')) {
          const src = this.code ?? ''
          this.html = Prism.highlight(src, Prism.languages[this.defaultGrammar], this.language_)
        }
      }
    }
  }

  // Rebuild line numbers after highlight or content change
  renderLineNumbers() {
    if (!this.pre) return
    // Remove existing line-numbers if present
    const existing = this.pre.querySelector('.line-numbers')
    if (existing) existing.remove()
    const lineNumbersDiv = document.createElement('div')
    lineNumbersDiv.className = 'line-numbers'
    this.pre.append(lineNumbersDiv)
    const lines = (this.html || '').split('\n')
    const lineStart = this.lineStart ?? 0
    for (let i = 0; i < lines.length; i++) {
      if (this.lineNumbers) {
        const span = document.createElement('span')
        span.className = 'line-number-span'
        span.innerHTML = `${lineStart + i + 1}`
        lineNumbersDiv.append(span)
      }
      const br = document.createElement('br')
      br.className = 'line-number-br'
      lineNumbersDiv.append(br)
    }
  }

  // Recompute when code or language changes
  recompute() {
    this.languageClass = `language-${this.language}`
    this.highlight()
    this.renderLineNumbers()
  }

  async copyCode() {
    try {
      const text = this.code ?? ''
      await navigator.clipboard.writeText(text)
      this.$store?.dispatch('updateSnackbar', {
        text: 'Code copied to clipboard',
        color: 'success',
        timeout: 2000
      })
    } catch (e) {
      console.error('Copy failed', e)
      this.$store?.dispatch('updateSnackbar', {
        text: 'Failed to copy',
        color: 'error',
        timeout: 3000
      })
    }
  }
}
</script>
<style lang="scss">

.line-numbers {
  position: absolute;
  top: 1.5em;
  left: 0em;
  width: .6em;
  border-right: 1px solid rgb(var(--v-theme-border-color));
  text-align: center;
}
.line-number-br {
  user-select: none;
}
.line-number-span {
  color: rgba(var(--v-theme-theme-on-code), var(--v-medium-emphasis-opacity));
  user-select: none;
}
pre[class*='language-'].pre {
  margin: 1rem auto;
  width: 100%;
  box-sizing: border-box;
  // max-width: 90%;
  padding-top: 1.5em;
  padding-bottom: 2em;
  position: relative;
  // .with-line-numbers {
  //   padding-left: 4.8em;
  // }
  // .without-line-numbers {
  //   padding-left: 1.8em;
  //   border-left: 1px solid rgb(var(--v-theme-border-color));
  // }
}
code[class*='language-'].code {
  white-space: pre-wrap;
}

.codeblock-container {
  position: relative;
}

.copy-btn-container {
  position: absolute;
  top: 6px;
  right: 8px;
  z-index: 3;
}
</style>
